import React, { useEffect, useState } from 'react';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useDashboard } from '@src/state/dashboard';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { RenderHtml } from '@src/common/RenderHtml';
import { Box } from '@uhg-abyss/web/ui/Box';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { event } from '@uhg-abyss/web/tools/event';

const ModalHeader = styled('h2', {
  fontSize: '2.027rem',
  fontWeight: 600,
  color: '$primary1',
  fontFamily: 'AbyssUHCSerif, Georgia Bold, serif',
});

const StyledRenderHtml = styled(RenderHtml, {
  width: '100%',
});

const StyledContentTableRenderHtml = styled(RenderHtml, {
  width: '100%',
  marginRight: '43px',
});

const StyledBox = styled(Box, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start !important',
  width: '100% !important',
});

const StyledModalSection = styled(Modal.Section, {
  '&.abyss-modal-section-root': {
    paddingBottom: '0px !important',
  },
});

const StyledLayoutGroup = styled(Layout.Group, {
  '&.abyss-layout-group > div:nth-child(1)': {
    paddingLeft: '20px',
  },
});

const StyledFooterButtonLayoutGroup = styled(Layout.Group, {
  '&.abyss-layout-group > div:nth-child(1)': {
    paddingRight: '12px',
  },
});

const StyledButton = styled(Button, { margin: '-4px' });

export const ChatBlockedModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [elasticApmTransaction, setElasticApmTransaction] = useState(null);
  const { viewedProviderChatModal } = useDashboard.state();
  const { setViewedProviderChatModal } = useDashboard.actions();
  const { getMessage, getFeature } = usePPDLagoon();
  const whiteListToggle = getFeature('CHAT_WHITELIST_MODAL');
  const whiteListMessage = getMessage('chat-whitelist-content');
  const whitelistMessageContentTable = getMessage(
    'chat-whitelist-content-table'
  );

  useEffect(() => {
    const validateChatWhitelisting = ({ detail }) => {
      if (!viewedProviderChatModal) {
        setIsOpen(detail.isBlocked);
        setViewedProviderChatModal(true);
        const eapm = window?.elasticApm;
        let transaction;
        if (eapm) {
          transaction = eapm.startTransaction(
            'Chat Whitelisting Modal',
            'chat-blocked-modal-open',
            { managed: false }
          );
        }
        event('CHAT_WHITELISTING_MODAL_OPEN', {
          isChatModalOpen: detail.isBlocked,
          transaction,
        });
        setElasticApmTransaction(transaction);
      }
    };
    document.addEventListener('onChatLoad', validateChatWhitelisting);

    return () => {
      document.removeEventListener('onChatLoad', validateChatWhitelisting);
    };
  }, [viewedProviderChatModal]);

  if (!whiteListToggle) {
    return null;
  }

  const copyToClipboard = async () => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = whitelistMessageContentTable;

    const thTexts = Array.from(tempElement.querySelectorAll('th')).map((th) =>
      th.textContent.trim()
    );

    const tdTexts = Array.from(tempElement.querySelectorAll('td')).map((td) =>
      td.textContent.trim()
    );

    const combinedText = thTexts
      .map((thText, index) => `${thText}\n          ${tdTexts[index]}\n`)
      .join('');

    const type = 'text/plain';
    const blob = new Blob([combinedText], { type });
    const data = [new window.ClipboardItem({ [type]: blob })];

    await navigator.clipboard.write(data);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (elasticApmTransaction) {
      elasticApmTransaction.end();
    }
  };

  return (
    <Modal
      data-testid='chat-whitelisting-modal'
      isOpen={isOpen}
      onClose={() => handleClose()}
      size={615}
      footer={
        <Modal.Section>
          <StyledFooterButtonLayoutGroup alignLayout='right'>
            <Button
              onClick={() => handleClose()}
              data-testid='chat-whitelisting-ok-button'
            >
              Ok
            </Button>
          </StyledFooterButtonLayoutGroup>
        </Modal.Section>
      }
    >
      <Modal.Section>
        <Layout.Group alignLayout='center'>
          <IconBrand
            icon='question_mark'
            size='72px'
            data-testid='icon-brand-communication-question-mark'
          />
        </Layout.Group>
      </Modal.Section>
      <Modal.Section>
        <Box color='white' padding='3px'>
          <ModalHeader>Not seeing our chat icon?</ModalHeader>
        </Box>
      </Modal.Section>
      <StyledModalSection>
        <Box color='white' padding='3px'>
          <StyledRenderHtml id='chat-whitelist-domain-info'>
            {whiteListMessage}
          </StyledRenderHtml>
        </Box>
      </StyledModalSection>
      <Modal.Section>
        <StyledBox color='white' padding='3px'>
          <StyledContentTableRenderHtml>
            {whitelistMessageContentTable}
          </StyledContentTableRenderHtml>
          <StyledLayoutGroup alignLayout='left' padding='20px'>
            <StyledButton
              onClick={() => copyToClipboard()}
              variant='tertiary'
              data-testid='chat-whitelisting-copy-button'
            >
              <IconSymbol icon='content_copy' /> Copy
            </StyledButton>
          </StyledLayoutGroup>
        </StyledBox>
      </Modal.Section>
    </Modal>
  );
};
